/**
 * Venue-contact department cards for Master Tour Calendar v2.
 * Buckets live CONTACTS-tab rows. Does not invent names, phones, or emails.
 */

export const CONTACT_DEPTS = [
  {
    key: 'ops',
    title: 'Operations & Production',
    hue: '#5a6fa8',
    keywords: ['production', 'rigger', 'dock', 'truck', 'ops', 'general manager', 'gm', 'advance'],
  },
  {
    key: 'sec',
    title: 'Security',
    hue: '#c0483c',
    keywords: ['security', 'guest services', 'barricade', 'pit', 'overnight', 'ada', 'watch'],
  },
  {
    key: 'hosp',
    title: 'Hospitality & Catering',
    hue: '#3f8f5f',
    keywords: ['catering', 'hospitality', 'green room', 'runner', 'dietary', 'allergy', 'buyout'],
  },
  {
    key: 'tech',
    title: 'Technical',
    hue: '#7c6cdd',
    keywords: ['audio', 'lighting', 'backline', 'foh', 'monitor', 'tech', 'stage', 'iem'],
  },
  {
    key: 'box',
    title: 'Box Office & Settlement',
    hue: '#c08133',
    keywords: ['box office', 'settlement', 'accounting', 'credentials', 'passes', 'ticket', 'boxoffice'],
  },
  {
    key: 'mkt',
    title: 'Marketing & Press',
    hue: '#9b59b6',
    keywords: ['marketing', 'press', 'photo', 'promo', 'publicity', 'pr '],
  },
];

export const CUSTOM_CAT_HUES = ['#2A9D8F', '#E9C46A', '#F4A261', '#9B5DE5', '#00BBF9', '#F15BB5'];

const DAY_OF_ROLES = [
  'production manager',
  'head audio',
  'box office',
  'settlement',
  'marketing manager',
  'general manager',
  'head of security',
];

export function emailFromBlob(blob) {
  const m = String(blob || '').match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return m ? m[0] : '';
}

export function phoneFromBlob(blob) {
  const m = String(blob || '').match(/(\+?\d[\d\s().-]{7,}\d)/);
  return m ? m[1].trim() : '';
}

export function hoursFromBlob(blob) {
  const s = String(blob || '');
  const range = s.match(/(\d{1,2}:\d{2}\s*[–\-]\s*\d{1,2}:\d{2})/);
  if (range) return range[1].replace(/\s+/g, '');
  const from = s.match(/((?:on site\s+)?from\s+\d{1,2}:\d{2}(?:\s*[ap]m)?)/i);
  if (from) return from[1];
  const mon = s.match(/(mon[^,]{0,18})/i);
  if (mon && /–|-/.test(mon[1])) return mon[1].trim();
  return '';
}

export function isDayOfContact(person) {
  const blob = `${person?.role || ''} ${person?.note || ''}`.toLowerCase();
  if (/day[-\s]?of/.test(blob)) return true;
  return DAY_OF_ROLES.some((r) => blob.includes(r));
}

function lineText(line) {
  return (line?.fields || []).map((f) => String(f || '').trim()).filter(Boolean).join(' — ');
}

function looksLikePhoneLine(text) {
  const digits = String(text || '').replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15 && Boolean(phoneFromBlob(text));
}

export function newContactId() {
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function newCategoryId() {
  return `cat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyContactCategory(partial = {}, index = 0) {
  return {
    id: String(partial.id || newCategoryId()),
    title: String(partial.title || '').trim(),
    hue: String(partial.hue || CUSTOM_CAT_HUES[index % CUSTOM_CAT_HUES.length]),
  };
}

export function emptyContact(partial = {}) {
  return {
    id: partial.id || newContactId(),
    name: String(partial.name || '').trim(),
    role: String(partial.role || '').trim(),
    note: String(partial.note || '').trim(),
    email: String(partial.email || '').trim(),
    phone: String(partial.phone || '').trim(),
    hours: String(partial.hours || '').trim(),
    deptKey: String(partial.deptKey || '').trim(),
  };
}

function parseDeptKeyLine(text) {
  const m = String(text || '').trim().match(/^dept:\s*(.+)$/i);
  return m ? m[1].trim() : '';
}

export function contactsFromStructured(raw, parseSetlistText) {
  const { songs } = parseSetlistText(String(raw || ''));
  return (songs || []).map((song, i) => {
    const details = (song.lines || []).map(lineText).filter(Boolean);
    const name = String(song.title || '').trim()
      || details.find((d) => !parseDeptKeyLine(d))
      || `Contact ${i + 1}`;
    let role = '';
    let email = '';
    let phone = '';
    let hours = '';
    let deptKey = '';
    const noteParts = [];
    details.forEach((detail, idx) => {
      const dk = parseDeptKeyLine(detail);
      if (dk) {
        deptKey = dk;
        return;
      }
      const em = emailFromBlob(detail);
      if (em && !email) {
        email = em;
        const leftover = detail.replace(em, '').replace(/^[—\s·,]+|[—\s·,]+$/g, '').trim();
        if (leftover) noteParts.push(leftover);
        return;
      }
      if (!phone && looksLikePhoneLine(detail)) {
        phone = detail.trim();
        return;
      }
      const hr = hoursFromBlob(detail);
      if (hr && !hours && /:/.test(detail) && detail.length <= 48) {
        hours = detail.trim();
        return;
      }
      if (!role && (song.title || idx === 0)) {
        role = detail;
        return;
      }
      noteParts.push(detail);
    });
    if (!role) role = 'Contact';
    return emptyContact({
      id: song.id || `c-${i}`,
      name,
      role,
      note: noteParts.join('\n'),
      email,
      phone,
      hours,
      deptKey,
    });
  }).filter((p) => p.name);
}

export function contactCategoriesFromStructured(raw) {
  try {
    const o = JSON.parse(String(raw || '').trim() || '{}');
    if (!Array.isArray(o.categories)) return [];
    return o.categories
      .map((c, i) => emptyContactCategory(c || {}, i))
      .filter((c) => c.id && c.title);
  } catch {
    return [];
  }
}

export function parseContactsDoc(raw, parseSetlistText) {
  return {
    people: contactsFromStructured(raw, parseSetlistText),
    categories: contactCategoriesFromStructured(raw),
  };
}

/** Compact lodging-rail blurb: one contact per line (name · phone or email). */
export function formatContactsSummary(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  let songs = [];
  try {
    const o = JSON.parse(s);
    if (o && Array.isArray(o.songs)) songs = o.songs;
  } catch {
    return '';
  }
  const lines = [];
  songs.forEach((song) => {
    const name = String(song.title || '').trim();
    const details = (song.lines || [])
      .map((ln) => (Array.isArray(ln?.fields) ? ln.fields.join(' ') : String(ln || '')))
      .map((t) => String(t || '').trim())
      .filter(Boolean);
    const email = details.map((d) => emailFromBlob(d)).find(Boolean) || '';
    const phone = details.map((d) => phoneFromBlob(d)).find(Boolean) || '';
    const bits = [name, phone || email].filter(Boolean);
    if (bits.length) lines.push(bits.join(' · '));
  });
  return lines.join('\n');
}

/** Persist venue-day contacts back to the CONTACTS tab JSON shape. */
export function contactsToStructured(people, categories = []) {
  const songs = (people || []).map((p, i) => {
    const lines = [];
    const push = (text) => {
      const t = String(text || '').trim();
      if (!t) return;
      lines.push({ id: `cl-${p.id || i}-${lines.length}`, fields: [t] });
    };
    if (p.deptKey) push(`Dept: ${p.deptKey}`);
    push(p.role);
    push(p.email);
    push(p.phone);
    push(p.hours);
    push(p.note);
    const title = String(p.name || '').trim();
    if (!title && !lines.length) return null;
    return {
      id: String(p.id || `c-${i}`),
      title,
      open: true,
      lines,
    };
  }).filter(Boolean);
  const cats = (categories || [])
    .map((c, i) => emptyContactCategory(c || {}, i))
    .filter((c) => c.id && c.title);
  if (!songs.length && !cats.length) return '';
  const payload = { v: 1, songs };
  if (cats.length) payload.categories = cats;
  return JSON.stringify(payload);
}

function deptForPerson(person, customKeys) {
  const explicit = String(person.deptKey || '').trim();
  if (explicit === 'other') return 'other';
  if (explicit && CONTACT_DEPTS.some((d) => d.key === explicit)) return explicit;
  if (explicit && customKeys.has(explicit)) return explicit;
  if (explicit) return 'other';
  const blob = `${person.role || ''} ${person.note || ''} ${person.name || ''}`.toLowerCase();
  let best = null;
  let score = 0;
  CONTACT_DEPTS.forEach((d) => {
    const hits = d.keywords.filter((k) => blob.includes(k)).length;
    if (hits > score) {
      score = hits;
      best = d.key;
    }
  });
  return score ? best : 'other';
}

export function bucketContacts(people, categories = []) {
  const custom = (categories || [])
    .map((c, i) => emptyContactCategory(c || {}, i))
    .filter((c) => c.id && c.title);
  const customKeys = new Set(custom.map((c) => c.id));
  const bags = {};
  CONTACT_DEPTS.forEach((d) => {
    bags[d.key] = [];
  });
  custom.forEach((c) => {
    bags[c.id] = [];
  });
  bags.other = [];
  (people || []).forEach((p) => {
    const key = deptForPerson(p, customKeys);
    (bags[key] || bags.other).push({
      ...p,
      primary: isDayOfContact(p),
    });
  });
  const depts = CONTACT_DEPTS.map((d) => {
    const list = bags[d.key] || [];
    const confirmed = list.some((p) => p.email || p.phone);
    return {
      key: d.key,
      title: d.title,
      hue: d.hue,
      people: list,
      status: list.length ? (confirmed ? 'Confirmed' : 'On file') : 'Open',
      custom: false,
    };
  });
  custom.forEach((c) => {
    const list = bags[c.id] || [];
    const confirmed = list.some((p) => p.email || p.phone);
    depts.push({
      key: c.id,
      title: c.title,
      hue: c.hue,
      people: list,
      status: list.length ? (confirmed ? 'Confirmed' : 'On file') : 'Open',
      custom: true,
    });
  });
  if (bags.other.length) {
    depts.push({
      key: 'other',
      title: 'Other',
      hue: '#6a6a65',
      people: bags.other,
      status: 'On file',
      custom: false,
    });
  }
  return depts;
}

/** Next show date on or after selected; else previous show. */
export function resolveShowDate(selectedDateKey, showDateKeys) {
  const keys = (showDateKeys || []).filter(Boolean).slice().sort();
  if (!keys.length) return selectedDateKey || '';
  if (keys.includes(selectedDateKey)) return selectedDateKey;
  const next = keys.find((k) => k >= selectedDateKey);
  if (next) return next;
  return keys[keys.length - 1];
}

export function formatDateLong(dateKey) {
  if (!dateKey) return '';
  const parts = String(dateKey).split('-').map(Number);
  if (parts.length !== 3) return dateKey;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function formatDateShort(dateKey) {
  if (!dateKey) return '';
  const parts = String(dateKey).split('-').map(Number);
  if (parts.length !== 3) return dateKey;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
